import React, { useRef, useState, useMemo } from 'react';
import ReactGA4 from 'react-ga4';
import { Map, Source, Layer } from 'react-map-gl/maplibre';
import maplibregl from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import 'maplibre-gl/dist/maplibre-gl.css';

// ... Tus otros imports (Zoom, Iconos, etc.) ...
import Zoom from '@mui/material/Zoom';
import VolunteerActivismSharpIcon from '@mui/icons-material/VolunteerActivismSharp';
import Box from '@mui/material/Box';

import MapLayers from './layers_selector/map-layers.js';

// 📍 1. IMPORTS DE LEYENDAS DESCOMENTADOS
import RiskSelector from './layers_selector/risk-selector.js'; // Asumo que este es para Convection/Kairos
import RiskSelectorCNN from './layers_selector/cnn/risk-selector_cnn.js';
import RiskSelectorCNN_WMS from './layers_selector/cnn/risk-selector_cnn_WMS.js';
import RiskSelectorVisibility from './layers_selector/visibility-layer/risk-selector_visibility.js';
import RiskSelectorNowcasting from './layers_selector/nowcasting-layer/risk-selector_nowcasting.js';
import RiskSelectorCNN_WMS_HEATMAP_Horizontal from './layers_selector/cnn/risk-selector_cnn_WMS_HEATMAP_horizontal.js';
import RiskSelectorCNN_VERTICAL from './layers_selector/cnn/risk-selector_cnn_vertical.js';
import RiskSelectorIce from './layers_selector/ice/risk-selector_ice.js';
import RiskSelectorTurbulence from './layers_selector/turbulence/risk-selector_turbulence.js';
import RiskSelectorDust from './layers_selector/dust/risk-selector_dust.js';

// Register PMTiles protocol
let pmtilesProtocolRegistered = false;

if (!pmtilesProtocolRegistered) {
  const protocol = new Protocol();
  maplibregl.addProtocol('pmtiles', protocol.tile.bind(protocol));
  pmtilesProtocolRegistered = true;
}

// Inicia WebWorkers incluso antes de renderizar el mapa - inicio 
maplibregl.prewarm();

// 📍 DEFINICIÓN DE TUS COORDENADAS
export const rectanguloByLayerName = {
  'KAIROS:EU_025_live_demo': [[73, -27], [33, 45]],
  'KAIROS:EU_0125_live_demo': [[60, -10], [36, 25]],
  'CNN:EU_025_live_demo': [[73, -27], [33, 45]],
  'CNN:EU_01_live_demo': [[64, -16], [33, 45]],
  'CNN:euro_1k_00625_15m': [[33, -10.0625], [56.4375, 21.6875]],
  'VISIBILITY:visibility_live_demo': [[73, -27], [27, 45]],
  'NOWCASTING:nowcastnet_live_demo': [[63.4, -11.6], [42.69, 15.85]],
  'ICE:ICE_025_live_demo': [[73, -27], [33, 45]],
  'TURBULENCE:TURBULENCE_0125_live_demo': [[60, -10], [36, 25]],
  'DUST:DUST_025_live_demo': [[63.25, -12.5], [-0.5, 51.25]]
};

function MapContainerComponent({
  selectedProbability,
  selectedTime,
  fetchGeoJSON,
  cqlFilter,
  forecastValue,
  formatForecastDate,
  cqlFilter2,
  cqlFilter3,
  activeGeoJsonLayer,
  onLayerChange,
  mapRef,
  onInitialTimeChange
}) {
  const [showGeoJson, setShowGeoJson] = useState(false);
  const [showMosaic, setShowMosaic] = useState(true);
  const [activeLayer, setActiveLayer] = useState('CNN:EU_025_live_demo');
  const [activeLayerName, setActiveLayerName] = useState('CNN');

  const isMobile = typeof window !== 'undefined' ? window.innerWidth <= 768 : false;

  const handleLayerChange = (layerName) => {
    setActiveLayer(layerName);
    onLayerChange(layerName);

    // 1. Lógica de nombres de capa
    switch (true) {
      case (layerName.includes('KAIROS')): setActiveLayerName('Convection'); break;
      case (layerName.includes('CNN')): setActiveLayerName('CNN'); break;
      case (layerName.includes('VISIBILITY')): setActiveLayerName('Visibility'); break;
      case (layerName.includes('NOWCASTING')): setActiveLayerName('Nowcasting'); break;
      case (layerName.includes('ICE')): setActiveLayerName('Ice'); break;
      case (layerName.includes('TURBULENCE')): setActiveLayerName('Turbulence'); break;
      case (layerName.includes('DUST')): setActiveLayerName('Dust'); break;
      default: setActiveLayerName('Convection'); break;
    }

    // 📍 LÓGICA DE CENTRADO
    const rawCoords = rectanguloByLayerName[layerName];
    if (rawCoords && mapRef.current) {
      const lat1 = rawCoords[0][0];
      const lng1 = rawCoords[0][1];
      const lat2 = rawCoords[1][0];
      const lng2 = rawCoords[1][1];

      const south = Math.min(lat1, lat2);
      const north = Math.max(lat1, lat2);
      const west = Math.min(lng1, lng2);
      const east = Math.max(lng1, lng2);

      const latSpan = north - south;
      const lngSpan = east - west;

      // 📍 FACTORES DE ZOOM POR CAPA
      const ZOOM_FACTOR_MAP = {
        'KAIROS:EU_025_live_demo': 0,
        'KAIROS:EU_0125_live_demo': 0,
        'CNN:EU_025_live_demo': 0.2,
        'CNN:EU_01_live_demo': 0.3,
        'CNN:euro_1k_00625_15m': 0.5,
        'VISIBILITY:visibility_live_demo': 2.8,
        'NOWCASTING:nowcastnet_live_demo': 0.35,
        'ICE:ICE_025_live_demo': 3,
        'TURBULENCE:TURBULENCE_0125_live_demo': 0.5,
        'DUST:DUST_025_live_demo': 0.25, // Factor base para DUST
      };

      // 📍 FUNCIÓN DE AJUSTE PARA DISPOSITIVOS
      let ZOOM_OUT_FACTOR = ZOOM_FACTOR_MAP[layerName] ?? 0.2;

      if (isMobile) {
        if (layerName.includes('VISIBILITY')) {
          ZOOM_OUT_FACTOR = 2.8;
        } else if (layerName.includes('ICE')) {
          ZOOM_OUT_FACTOR = 3.0;
        } else if (layerName.includes('CNN:EU_01')) {
          ZOOM_OUT_FACTOR = 0.15;
        } else if (ZOOM_OUT_FACTOR === 0) {
          ZOOM_OUT_FACTOR = 0.15;
        } else {
          ZOOM_OUT_FACTOR = ZOOM_OUT_FACTOR * 1.2;
        }
      }

      const effectiveLatSpan = Math.max(latSpan, 30);

      let latPadding = (effectiveLatSpan * ZOOM_OUT_FACTOR) / 2;
      let lngPadding = isMobile ? 0 : (lngSpan * ZOOM_OUT_FACTOR) / 2;

      // 🚨 AJUSTE EN ESCRITORIO / PANTALLAS GRANDES PARA DUST
      if (!isMobile && layerName.includes('DUST')) {
        // Reducimos ligeramente el padding geométrico para evitar que la caja de destino 
        // se calcule excesivamente hacia el oeste (izquierda)
        lngPadding = lngPadding * 0.5;
      }

      const targetSouth = Math.max(-85, south - latPadding);
      const targetNorth = Math.min(85, north + latPadding);
      const targetWest = west - lngPadding;
      const targetEast = east + lngPadding;

      // 📍 REESTRUCTURACIÓN DEL PADDING DE PANTALLAS GRANDES
      let currentPadding;
      if (isMobile) {
        currentPadding = {
          top: layerName.includes('DUST') ? 10 : 20,
          bottom: 10,
          left: 10,
          right: 10
        };
      } else {
        // Configuramos DUST por objeto explícito en escritorio para evitar el colapso horizontal
        if (layerName.includes('DUST')) {
          currentPadding = {
            top: 60,
            bottom: 60,
            left: 120,  // Reducido de 300 a 120 para darle espacio real al mapa y evitar que rebote
            right: 120
          };
        } else {
          const PADDING_MAP = {
            'CNN:EU_025_live_demo': 150,
            'CNN:EU_01_live_demo': 50,
          };
          const desktopPadding = PADDING_MAP[layerName] ?? 0;
          currentPadding = {
            top: desktopPadding / 2,
            bottom: desktopPadding / 2,
            left: desktopPadding,
            right: desktopPadding
          };
        }
      }

      // 📍 DETERMINAR TIPO DE ANIMACIÓN SEGURO
      // Si es DUST en pantalla grande, usamos una transición lineal o intermedia para erradicar el salto elíptico
      const usarLinear = isMobile || layerName.includes('DUST');

      mapRef.current.fitBounds(
        [
          [targetWest, targetSouth],
          [targetEast, targetNorth]
        ],
        {
          padding: currentPadding,
          duration: isMobile ? 2000 : 4000, // Un poco más rápido en Dust para mitigar retrasos visuales
          essential: true,
          animate: true,
          linear: usarLinear, // 🧠 CLAVE: Si es true, el mapa se desplaza en línea recta matemática eliminando el "latigazo" de arco
        }
      );
    }
  };

  // 📍 LÓGICA DE MÁSCARA
  const DEFAULT_BOUNDS = [[73, -27], [33, 45]];
  const { navigationBounds, maskData } = useMemo(() => {
    const rawCoords = rectanguloByLayerName[activeLayer] || DEFAULT_BOUNDS;

    const lat1 = rawCoords[0][0];
    const lng1 = rawCoords[0][1];
    const lat2 = rawCoords[1][0];
    const lng2 = rawCoords[1][1];

    const north = Math.max(lat1, lat2);
    const south = Math.min(lat1, lat2);
    const east = Math.max(lng1, lng2);
    const west = Math.min(lng1, lng2);

    const PADDING = 100;

    const navBounds = [
      west - PADDING,
      Math.max(-90, south - PADDING),
      east + PADDING,
      Math.min(90, north + PADDING)
    ];

    const maskGeoJSON = {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [
          [[-180, -90], [180, -90], [180, 90], [-180, 90], [-180, -90]],
          [[west, north], [east, north], [east, south], [west, south], [west, north]]
        ]
      }
    };

    return { navigationBounds: navBounds, maskData: maskGeoJSON };
  }, [activeLayer]);


  // 📍 3. ZOOM INICIAL AJUSTADO
  const [viewState, setViewState] = useState({
    longitude: 9.00,
    latitude: 60.00, // Centrado un poco más abajo para ver mejor Europa al inicio
    zoom: 0.4 // Zoom muy lejano para ver casi todo el mapa al cargar
  });

  const mapContainerDivRef = useRef(null);

  return (
    <div ref={mapContainerDivRef} style={{ height: '100%', width: '100%', position: 'relative', backgroundColor: '#ddd' }}>
      <Map
        ref={mapRef}
        mapLib={maplibregl}
        reuseMaps={true}
        {...viewState}
        onMove={evt => setViewState(evt.viewState)}
        style={{ width: '100%', height: '100%' }}
        mapStyle="https://basemaps.cartocdn.com/gl/positron-gl-style/style.json"
        maxBounds={navigationBounds}
        fadeDuration={0}
        minZoom={isMobile ? 1.5 : ((activeLayer.includes('CNN:EU_01') || activeLayer.includes('DUST')) ? 2.5 : 2)}
        validateStyle={false} //Ahorra CPU al navegador
        antialias={false}
      >
        <Source id="mask-source" type="geojson" data={maskData}>
          <Layer
            id="mask-layer-fill"
            type="fill"
            paint={{
              'fill-color': '#000000',
              'fill-opacity': 0.25
            }}
          />
          <Layer
            id="mask-layer-outline"
            type="line"
            paint={{
              'line-color': '#555555',
              'line-width': 1.5,
              'line-dasharray': [2, 1],
              'line-opacity': 0.5
            }}
          />
        </Source>

        <div
          onClick={() => {
            ReactGA4.event({ category: 'UI', action: 'Map Layers Button clicked' });
          }}
          style={{
            position: 'absolute',
            bottom: '30px',
            left: '10px',
            zIndex: 1001,
            width: 'auto',
            pointerEvents: 'auto'
          }}
        >
          <MapLayers
            activeMapBaseLayer={'light_all'}
            layerOpacity={0.69}
            cqlFilter={cqlFilter}
            onLayerChange={handleLayerChange}
            onInitialTimeChange={onInitialTimeChange}
            showGeoJson={showGeoJson}
            setShowGeoJson={setShowGeoJson}
            showMosaic={showMosaic}
            setShowMosaic={setShowMosaic}
          />
        </div>

      </Map>

      {/* 📍 4. VISUALIZACIÓN DE LEYENDAS (RISK SELECTORS) */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 180, sm: 90, md: 84, lg: 80 },
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          zIndex: 1002,
          pointerEvents: 'none',
          px: 1
        }}
      >
        <Box sx={{ pointerEvents: 'auto' }}>
          {activeLayerName === 'Convection' && <RiskSelector />}
          {activeLayerName === 'CNN' &&
            !(activeLayer === 'CNN:EU_025_live_demo' || activeLayer === 'CNN:EU_01_live_demo' || activeLayer === 'CNN:euro_1k_00625_15m') && (
              <RiskSelectorCNN_WMS />
            )}
          {activeLayerName === 'CNN' &&
            (activeLayer === 'CNN:EU_025_live_demo' || activeLayer === 'CNN:EU_01_live_demo' || activeLayer === 'CNN:euro_1k_00625_15m') && (
              <>
                {showMosaic && <RiskSelectorCNN_WMS_HEATMAP_Horizontal />}
                {showGeoJson && <RiskSelectorCNN_VERTICAL />}
              </>
            )}
          {activeLayerName === 'Visibility' && <RiskSelectorVisibility />}
          {activeLayerName === 'Nowcasting' && <RiskSelectorNowcasting />}
          {activeLayerName === 'Ice' && <RiskSelectorIce />}
          {activeLayerName === 'Turbulence' && <RiskSelectorTurbulence />}
          {activeLayerName === 'Dust' && <RiskSelectorDust />}
        </Box>
      </Box>

      <div className="feedback" style={{ position: 'absolute', bottom: 30, right: 10, zIndex: 1005 }}>
        <a href="https://www.aimethods.com/feedback" target="_blank" rel="noopener noreferrer">
          <VolunteerActivismSharpIcon fontSize="medium"
            style={{
              color: 'white',
              backgroundColor: 'rgba(33, 124, 11, 0.9)',
              borderRadius: '20%',
              padding: '5px',
              border: '1.75px solid rgba(0, 0, 0, 1)',
            }} />
        </a>
      </div>

    </div>
  );
}

export default MapContainerComponent;